// app/javascript/components/SupplyRequestsIndex.tsx
import * as React from "react";
import {
  Grid,
  Box,
  Stack,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Chip,
  IconButton,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CreateIcon from "@mui/icons-material/Create";
import EditIcon from "@mui/icons-material/Edit";
import AddSupplyRequestModal, { SupplyItem } from "../patient_encounters/AddSupplyRequestModal";
import SupplyRequestsElement from "./SupplyRequestsElement"; // <-- Imported new component

// helpers and setting imports
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext, ImagesContext } from "../Context";
import LoadingTable from "../shared/LoadingTable";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import { useLocation } from "react-router-dom";

interface Props {
  selected_customer?: any;
}

const SupplyRequestsIndex: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const location = useLocation();
  const afterFetch = React.useRef(false);
  const selectedCustomerId =
    props?.selected_customer?.customer_id ||
    props?.selected_customer?.customer?.id ||
    null;

  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [selectedRequest, setSelectedRequest] = React.useState<any>(null);
  const [editModalPayerNames, setEditModalPayerNames] = React.useState<string[]>([]);

  // data + ui state
  const [requests, setRequests] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  // pagination state
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const perPage = 8;
  const [availableItems, setAvailableItems] = React.useState<string[]>([]);
  const [availableItemObjects, setAvailableItemObjects] = React.useState<SupplyItem[]>([]);

  React.useEffect(() => {
    fetch("/data_fetching/supply_items.json")
      .then(res => res.json())
      .then(data => {
        const items: SupplyItem[] = data.items || [];
        setAvailableItemObjects(items);
        setAvailableItems(items.map((i) => i.name));
      });
  }, []);

  // Read initial filter values from URL query params (e.g. when navigating from the
  // Health Navigator Summary Report). payer_id is accepted here but the supply
  // requests API does not yet filter by payer — it is a known gap tracked separately.
  const initialUrlParams = new URLSearchParams(location.search);

  // filter state
  const [showCompleted, setShowCompleted] = React.useState<boolean>(false);
  const [lastName, setLastName] = React.useState<string>("");
  const [requestDateFrom, setRequestDateFrom] = React.useState<string>(
    initialUrlParams.get("request_date_from") || ""
  );
  const [requestDateTo, setRequestDateTo] = React.useState<string>(
    initialUrlParams.get("request_date_to") || ""
  );

  // sort state
  const [sortObject, setSortObject] = React.useState<any>({
    field: "request_date",
    direction: "descending",
  });

  const darkHeaderStyle = {
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontWeight: 700,
};

  React.useEffect(() => {
    setCurrentPage(1);
    afterFetch.current = false;
  }, [selectedCustomerId]);

  React.useEffect(() => {
    fetchData();
  }, [
    currentPage,
    showCompleted,
    lastName,
    requestDateFrom,
    requestDateTo,
    sortObject,
    selectedCustomerId,
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("per_page", perPage.toString());
      params.append("show_completed", showCompleted.toString());
      params.append("sort_by", sortObject.field);
      params.append("sort_direction", sortObject.direction);
      if (selectedCustomerId) {
        params.append("customer_id", selectedCustomerId.toString());
      }
      if (lastName && lastName.trim().length > 0) {
        params.append("search", lastName.trim());
      }
      if (requestDateFrom) {
        params.append("request_date_from", requestDateFrom);
      }
      if (requestDateTo) {
        params.append("request_date_to", requestDateTo);
      }

      const response = await fetch(
        `/data_fetching/supply_requests.json?${params.toString()}`,
        {
          method: "GET",
          headers: getHeaders(authenticationSetting.csrfToken),
        }
      );

      const result = await response.json();

      if (result?.success && result?.resource?.supply_requests) {
        const fetchedRequests = result.resource.supply_requests || [];
        setRequests(fetchedRequests);

        // The Rails controller returns: resource: { supply_requests, pagination: { page, per_page, total_count, has_more } }
        const pagination = result.resource.pagination || {};
        const fetchedTotalCount = Number(pagination.total_count) || 0;
        const effectivePerPage = Number(pagination.per_page) || perPage;
        const computedPageCount =
          effectivePerPage > 0
            ? Math.max(1, Math.ceil(fetchedTotalCount / effectivePerPage))
            : 1;
        setTotalCount(fetchedTotalCount);
        setPageCount(fetchedTotalCount > 0 ? computedPageCount : 0);
      } else {
        setRequests([]);
        setTotalCount(0);
        setPageCount(0);
      }
    } catch (err) {
      console.error("Failed to fetch supply requests", err);
      setRequests([]);
      setTotalCount(0);
      setPageCount(0);
    } finally {
      setLoading(false);
      afterFetch.current = true;
    }
  };

  const handleOpenEditModal = (request: any) => {
    setSelectedRequest(request);
    setEditModalPayerNames([]);
    setEditModalOpen(true);
    if (request?.patient_id) {
      fetch(`/data_fetching/patient_payer/${request.patient_id}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.success && Array.isArray(data?.resource?.payer_names)) {
            setEditModalPayerNames(data.resource.payer_names);
          }
        })
        .catch(() => {});
    }
  };

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    setSortObject(sort);
  };

  const handleStatusChange = async (event: any, newStatus: string, requestId: number) => {
    if (event) event.preventDefault();
    
    try {
      const response = await fetch(`/data_fetching/supply_requests/${requestId}`, {
        method: "PATCH",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          supply_request: {
            status: newStatus
          }
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        fetchData(); 
      } else {
        alert(result.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status.");
    }
  };

  return (
    <div className="immunization-container">
      <div className="immunization-header-section">
        <p className="immunization-header-text">Supply Requests</p>
      </div>
      <Grid container className="panel-container" borderRadius={"4px"}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                  >
                    <Grid
                      item
                      xs={3}
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                    >
                      <p className="immunization-result">
                        {showCompleted ? "All Requests" : "Active Requests"}
                      </p>
                      {totalCount > 0 && (
                        <p className="immunization-count-container badge">
                          {totalCount}
                        </p>
                      )}
                    </Grid>
                    <Grid
                      xs={10}
                      item
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"flex-end"}
                      gap={2.5}
                    >
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Switch
                          checked={showCompleted}
                          onChange={(e) => {
                            setShowCompleted(e.target.checked);
                            setCurrentPage(1);
                          }}
                          color="primary"
                          size="small"
                        />
                        <p
                          style={{
                            color: "#757575",
                            fontWeight: 500,
                            fontSize: "13px",
                            margin: 0,
                          }}
                        >
                          Show Completed Requests
                        </p>
                      </Box>

                      <TextField
                        id="last_name"
                        className="textInput plainLabel"
                        placeholder="Search Last Name"
                        value={lastName}
                        onChange={(event) => {
                          setLastName(event.target.value);
                          setCurrentPage(1);
                        }}
                        inputProps={{ "aria-label": "search last name" }}
                        variant="outlined"
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            borderRadius: "4px",
                            height: 42.25,
                            backgroundColor: "#EFE9E7",
                            width: 240,
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          width: 240,
                          flex: "0 0 auto",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { border: "none" },
                            "&:hover fieldset": { border: "none" },
                            "&.Mui-focused fieldset": { border: "none" },
                          },
                          "& .MuiInputBase-input": {
                            color: "#1E1E1E",
                            fontSize: 12,
                            fontWeight: 500,
                            "&::placeholder": {
                              color: "#868382",
                              fontSize: 12,
                              fontWeight: 500,
                              opacity: 1,
                            },
                          },
                        }}
                      />

                      {/* Request Date Range Filter */}
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ minWidth: 0 }}
                      >
                        <Box sx={{ width: 150 }}>
                          <DatePickerWithLabel
                            date={requestDateFrom}
                            setDate={(date: string) => {
                              setRequestDateFrom(date);
                              setCurrentPage(1);
                            }}
                            label="Request Date From"
                            initialDate=""
                            maxDate={requestDateTo || undefined}
                          />
                        </Box>
                        <Box sx={{ width: 150 }}>
                          <DatePickerWithLabel
                            date={requestDateTo}
                            setDate={(date: string) => {
                              setRequestDateTo(date);
                              setCurrentPage(1);
                            }}
                            label="Request Date To"
                            initialDate=""
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>

              <Grid container className="general-patient-info-container">
                <Grid
                  item
                  xs={12}
                  style={{
                    backgroundColor: "white",
                    display: "flex",
                    width: "100%",
                    flexDirection: "column",
                  }}
                >
                  {loading ? (
                    <div style={{ marginTop: "24px" }}>
                      <LoadingTable />
                    </div>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: "100%" }}>
                        <Table stickyHeader className="no-border-table">
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setSortOrder(
                                    "patient_name",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                                className="table-header name-header"
                                sx={{ paddingLeft: "24px !important" }}
                                width="22%"
                              >
                                <div className="row">
                                  <p className="table-header" style={darkHeaderStyle}>
                                    Patient Name (Encounter)
                                  </p>
                                  {sortObject.field == "patient_name" ? (
                                    <span className="sortIndicator">
                                      {sortObject.direction == "ascending" ? (
                                        <img
                                          src={images.sort_ascending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Asc"
                                        />
                                      ) : (
                                        <img
                                          src={images.sort_descending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Desc"
                                        />
                                      )}
                                    </span>
                                  ) : (
                                    <span className="sortIndicator">
                                      <img
                                        src={images.sort_plain_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setSortOrder(
                                    "supply_request_item",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                                className="nowrap-header"
                                width="18%"
                              >
                                <div className="row">
                                  <p className="table-header" style={darkHeaderStyle}>Item</p>
                                  {sortObject.field == "supply_request_item" ? (
                                    <span className="sortIndicator">
                                      {sortObject.direction == "ascending" ? (
                                        <img
                                          src={images.sort_ascending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Asc"
                                        />
                                      ) : (
                                        <img
                                          src={images.sort_descending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Desc"
                                        />
                                      )}
                                    </span>
                                  ) : (
                                    <span className="sortIndicator">
                                      <img
                                        src={images.sort_plain_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                align="left"
                                className="nowrap-header"
                                width="6%"
                              >
                                <p className="table-header" style={darkHeaderStyle}>Qty</p>
                              </TableCell>
                              <TableCell
                                align="left"
                                className="nowrap-header"
                                width="28%"
                              >
                                <p className="table-header" style={darkHeaderStyle}>Description</p>
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setSortOrder(
                                    "request_date",
                                    sortObject.direction == "ascending"
                                      ? "descending"
                                      : "ascending"
                                  );
                                }}
                                style={{ cursor: "pointer" }}
                                className="nowrap-header"
                                width="12%"
                              >
                                <div className="row">
                                  <p className="table-header" style={darkHeaderStyle} >Request Date</p>
                                  {sortObject.field == "request_date" ? (
                                    <span className="sortIndicator">
                                      {sortObject.direction == "ascending" ? (
                                        <img
                                          src={images.sort_ascending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Asc"
                                        />
                                      ) : (
                                        <img
                                          src={images.sort_descending_src}
                                          width="10"
                                          className="sort-icon"
                                          alt="Sort Desc"
                                        />
                                      )}
                                    </span>
                                  ) : (
                                    <span className="sortIndicator">
                                      <img
                                        src={images.sort_plain_src}
                                        width="10"
                                        className="sort-icon"
                                        alt="Sort Asc"
                                      />
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell
                                align="center"
                                className="nowrap-header"
                                width="10%"
                              >
                                <p className="table-header" style={darkHeaderStyle}>Status</p>
                              </TableCell>
                              <TableCell
                                align="left"
                                className="nowrap-header"
                                width="8%"
                              >
                                <p className="table-header" style={darkHeaderStyle}>Tracking Info</p>
                              </TableCell>
                              <TableCell
                                align="center"
                                className="nowrap-header"
                                sx={{ paddingRight: "16px !important" }}
                                width="8%"
                                aria-label="Edit"
                              />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {requests.length > 0 ? (
                              <>
                                {requests.map((req, index) => (
                                  <TableRow
                                    key={req.id || index}
                                    className={
                                      index % 2 == 0 ? "rowEven" : "row"
                                    }
                                  >
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                      sx={{ paddingLeft: "24px !important" }}
                                    >
                                      <Link
                                        className={"table-body-link"}
                                        href={`/patient_reports/${req.patient_id}/patient_encounters/new?encounter_id=${req.encounter_billing_uuid}`}
                                      >
                                        {req.patient_name}
                                      </Link>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {req.supply_request_item}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {req.item_quantity}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {req.description}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {req.request_date
                                          ? new Date(
                                              req.request_date + "T12:00:00"
                                            ).toLocaleDateString("en-US", {
                                              year: "numeric",
                                              month: "2-digit",
                                              day: "2-digit",
                                            })
                                          : "--"}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="center"
                                      className="tableCell"
                                    >
                                      <SupplyRequestsElement
                                        currentStatus={req.status}
                                        onStatusChange={(e: any, status: string) => handleStatusChange(e, status, req.id)}
                                      />
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="center"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {req.tracking_number || "--"}
                                      </p>
                                    </TableCell>
                                    {/* Edit-icon column (no header). */}
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="center"
                                      className="tableCell"
                                      sx={{ paddingRight: "15px !important" }}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        <CreateIcon
                                          onClick={() =>
                                            handleOpenEditModal(req)
                                          }
                                          sx={{
                                            color: "black",
                                            cursor: "pointer",
                                            "&:hover": {
                                              backgroundColor: "transparent",
                                              color: "black",
                                            },
                                          }}
                                        />
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </>
                            ) : (
                              afterFetch.current && (
                                <TableRow>
                                  <TableCell colSpan={8} align="center">
                                    <p>No supply requests found.</p>
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {pageCount > 0 && (
                        <Pagination
                          count={pageCount}
                          page={currentPage}
                          onChange={(_event: React.ChangeEvent, page: number) =>
                            setCurrentPage(page)
                          }
                          sx={{
                            margin: "0 auto",
                            paddingTop: "16px",
                            marginBottom: "10px",
                          }}
                          boundaryCount={3}
                        />
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <AddSupplyRequestModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedRequest(null);
        }}
        encounterId={selectedRequest?.encounter_billing_id}
        payerNames={editModalPayerNames}
        availableItems={availableItems}
        availableItemObjects={availableItemObjects}
        onSaveSuccess={fetchData}
        isEdit={true}
        existingData={selectedRequest}
      />
    </div>
  );
};

export default SupplyRequestsIndex;